import React, { useMemo } from 'react';
import {
  shallowEqual,
  useSelector,
} from 'react-redux';

import {
  coreSelectors,
} from '@gymcloud/core';

import {
  Modal,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import BasicForm from './BasicForm';
import { FormData } from './utils';

const mapState = state => ({
  propertiesMap: coreSelectors.getResourceMap('personalProperties')(state),
  propertyOptions: coreSelectors.getVisiblePropertyOptions(state),
});

const defaultInitialValues: FormData = {
  range: '',
  metric: '',
  rawValue: '',

  options: [],
};

type Props = {
  isOpen: boolean;
  name: string;
  exercise: any;
  onRequestSubmit: (data: any, actions: any) => void;
  onRequestClose: () => void;
};

const DieRollSetupModal = ({
  isOpen,
  name,
  exercise,
  onRequestSubmit,
  onRequestClose,
}: Props) => {
  const { propertiesMap } = useSelector(mapState, shallowEqual);
  const [
    initialValues,
    propertyOptions,
  ] = useMemo(() => {
    if (!exercise?.properties?.length) {
      return [defaultInitialValues, []];
    }

    const propertyUniqueByKey = [
      ...new Map(exercise.properties.map((p: any) => [p.personal_property.id, p])).values(),
    ];
    const options = propertyUniqueByKey.map(
      (p: any) => ({
        id: p.id,
        value: p.personal_property.id,
        label: p.personal_property.name,
        unitId: p.property_unit_id,
      }),
    );
    let values = {
      ...defaultInitialValues,
      metric: options[0].value,
    };

    const hasOptions = exercise.properties.filter(p => p.dieroll_options?.length > 0);
    if (hasOptions.length) {
      const dieRollOptions = hasOptions.reduce((acc, p) => ([
        ...acc,
        ...p.dieroll_options.map(
          option => ({
            id: option.id,
            metric: p.personal_property.id,
            value: option.value ? Math.round(option.value) : 0,
            value2: option.value2 ? Math.round(option.value2) : 0,
            dieroll_range_end: option.dieroll_range_end,
            dieroll_range_start: option.dieroll_range_start,
          }),
        ),
      ]), []);

      values = {
        ...values,
        options: dieRollOptions,
      };
    }

    return [values, options];
  }, [exercise?.properties]);

  return (
    <Modal
      title="Die Roll Setup"
      contentStyle={{
        width: '600px',
      }}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <Flex spacing="20" column width="100%">
        <Text size="16px" color="#808084" weight="400">{name}</Text>
        <BasicForm
          initialValues={initialValues}
          propertiesMap={propertiesMap}
          propertyOptions={propertyOptions}
          onCancel={onRequestClose}
          onSubmit={(options, actions) => {
            const data = propertyOptions
              .filter(option => options.some(o => o.metric === option.value))
              .map(option => ({
                id: option.id,
                property_unit_id: option.unitId,
                workout_exercise_id: exercise.id,
                personal_property_id: option.value,
                dieroll_options_attributes: options
                  .filter(
                    o => o.metric === option.value,
                  )
                  .map(({ metric, ...rest }) => rest),
              }));

            onRequestSubmit(data, actions);
          }}
        />
      </Flex>
    </Modal>
  );
};

export default DieRollSetupModal;
